/**
 * Scenarios and the technical introduction to Emerging World Models.
 *
 * Content source: the `ewm-state-machine` research repository — its README and
 * the sixteen notebooks under `notebooks/`. Every number on these pages is
 * traceable to a named notebook run, and the limits travel with the numbers.
 *
 * Editorial rules (same as data/ewm.js):
 *   - every figure is traceable to a named run;
 *   - caveats sit next to the claims they qualify;
 *   - no hardware marketing — the memory layer is the subject, not the silicon.
 */

/* ------------------------------------------------------------------ */
/*  The four ideas the whole model hangs on (from the repository)      */
/* ------------------------------------------------------------------ */

export const EWM_IDEAS = [
  {
    id: 1,
    name: 'One universe',
    body:
      'Every state is a node of the same Boolean lattice over the bit plane: subset order, union as join, intersection as meet, symmetric difference as ring addition. Nothing new is created — a computation names a lattice expression and discovers the node.',
  },
  {
    id: 2,
    name: 'The morphism is the contract',
    body:
      'The connection from tokens to bits is a chosen morphism. Only three properties are required — Idempotence, Immutability and Content Addressability (IICA) — and the bit vector forgets provenance by design, so any IICA algorithm is interchangeable.',
  },
  {
    id: 3,
    name: 'The fiber is global',
    body:
      'The same bit in any state points to the same lookup fiber: the fiber is a property of the bit address, never of the particular state. Materialization is exact on the builders and probabilistic on the extras.',
  },
  {
    id: 4,
    name: 'Decompositions are frames',
    body:
      'Every decomposition is a named set of dimensions — departed/retained/new, joined perceptrons, the ring basis. Projecting a state onto a frame gives coordinates, and the sequence of coordinates over time is the state trajectory.',
  },
];

/* ------------------------------------------------------------------ */
/*  Vocabulary — the terms a reader meets on these pages               */
/* ------------------------------------------------------------------ */

export const EWM_VOCABULARY = [
  { term: 'S(t)', meaning: 'the current emerging state — the tip of the stack, work in progress' },
  { term: 'H(t-1)', meaning: 'the previous committed state — the cache required to commit the next one' },
  { term: 'D / R / N', meaning: 'departed, retained and new bits between two states' },
  { term: 'HLLSet', meaning: 'the bit-set state itself: union, intersection, difference and population count' },
  { term: 'tid', meaning: 'the token or id form of an encoding — what ingest lifts into bits' },
  { term: 'morphism', meaning: 'the ingest or materialize step that moves between tokens and bits' },
  { term: 'content key', meaning: 'the hash that names a state; equal states share a key' },
  { term: 'frame', meaning: 'a named set of dimensions used to read coordinates off a state' },
  { term: 'BSS', meaning: 'a similarity between two states, |A ∩ B| / |B| — defined for any pair' },
  { term: '[UM]', meaning: 'the processing unit: stateless, disposable, owns only a store handle' },
];

/* ------------------------------------------------------------------ */
/*  The loop — encodings in, ordered encodings out                     */
/* ------------------------------------------------------------------ */

export const SIDECAR_LOOP = [
  { id: 1, step: 'Encodings in', detail: 'The host produces dense vectors per step. No text round-trip, no prompt surgery.' },
  { id: 2, step: 'Quantize', detail: 'A shared codebook maps each vector to a tid. This is the only vocabulary-aware step.' },
  { id: 3, step: 'Ingest', detail: 'Tokens are lifted into bit sets. Three channels give every token three independent addresses.' },
  { id: 4, step: 'State', detail: 'Committed state outside the unit, with D / R / N against the previous state and a ring basis.' },
  { id: 5, step: 'Materialize', detail: 'Bits are lowered back to tokens, in the original order, keeping every candidate.' },
  { id: 6, step: 'Back to host', detail: 'The host gets its own encodings back, plus whatever the side-car measured about them.' },
];

/* ------------------------------------------------------------------ */
/*  The scenarios                                                      */
/* ------------------------------------------------------------------ */

export const SCENARIOS = [
  {
    id: 'ingest-materialize',
    icon: '🔁',
    name: 'The core loop',
    tagline: 'Tokens in, bits, then the same tokens back in the same order.',
    hosts: ['No host needed — the notebook is the application'],
    problem:
      'A model produces a stream of encodings and keeps nothing. There is no addressable state, and no way to ask what a turn added or removed.',
    approach:
      'Ingest lifts a token collection into a bit-set state and returns the hash that names it, preserving the work in three lookup channels. Materialize lowers it back, ordered by default.',
    measured: [
      { value: '1.0', label: 'ordered restore — the sequence comes back exactly' },
      { value: '3', label: 'independent addresses per token: 1-, 2- and 3-gram channels' },
      { value: '0', label: 'residual on a replayed turn — it is already inside the span' },
    ],
    evidence: [
      'Ingest returns the content key of the new state, plus one key per channel. The three channels hold the 1-gram, 2-gram and 3-gram views of the same tokens.',
      'A bit is anonymous: it does not record which token or which scheme set it. Collisions are harmless because the materializer disambiguates using the state it is asked to restore.',
      'A gate extracts one channel from a state — the intersection of the channel with the state — and returns that state intact when the channel is a superset. Verified for both token schemes.',
      'Replaying a turn already in the context window produces no new information: residual zero, in-span true. A genuinely fresh turn reports the bits it adds.',
      'A crash costs a pop, not a rebuild: the unit and its cache are discarded, the persistent store is untouched, and a fresh unit resumes from the tip and commits again.',
      'The commit rule is broader than new content. A turn commits when it brings new bits or when the ring basis changes — a basis change is a structural event, because the interpretation changed even if the content did not.',
    ],
    caveats: [
      'Materialization keeps every candidate rather than filtering on a threshold, so a heavily collided bit restores all of its possibilities. That is restoration by probability, not by confidence score.',
    ],
    framework: ['IICA morphisms', 'One universe', 'Frame readings'],
    notebooks: ['01_ingest_materialize_um.ipynb'],
  },
  {
    id: 'scene-sidecar',
    icon: '🎬',
    name: 'Video and scene understanding',
    tagline: 'Which frame starts a new scene, and exactly what changed between two frames.',
    hosts: ['A vision tower producing per-patch embeddings', 'A 16×16 patch grid per frame'],
    problem:
      'A vision host encodes each frame and forgets it. Detecting a scene change, and reporting what left the frame and what arrived, is left to a similarity number per frame.',
    approach:
      'Patch encodings are quantized and ingested per frame. The side-car reports departed, retained and new patches, and restores the full patch grid in its original order.',
    measured: [
      { value: '9/9', label: 'scene cuts found by the transition decomposition' },
      { value: '1.0', label: 'positional accuracy restoring the 16×16 grid' },
      { value: '0.935', label: 'agreement between the host similarity line and the state line' },
      { value: '256', label: 'patches restored per frame, no false positives' },
    ],
    evidence: [
      'The host similarity line found six of nine cuts. The state-to-state similarity line found all nine, and agreed with the host on the six.',
      'A cut and an ordinary in-frame transition separate cleanly by the decomposition. One cut measured 530 departed, 51 retained, 188 new patches; an in-frame transition measured 10 / 568 / 14.',
      'A transition can be restored as three subframes — departed, retained and new — each with its own token set and its own positions. One cut: 205 departed patches, 9 retained, 75 new.',
      'Frame-level moving averages at three channel widths cross downward together exactly at the cuts, including the cuts the host line missed.',
      'Basis history is recorded, so any frame can be re-read through the interpretation of an earlier frame. The measured error of that re-reading is the spill — for the final frame, 306 bits that the first interpretation cannot see.',
    ],
    caveats: [
      'The nine-of-nine figure is the decomposition line. On the real quantized clip the separation still holds at roughly 3.9×, but in-scene variance is high, so a strict two-sigma novelty threshold flags only three of nine cuts.',
      'The reason is structural, not a defect: the codebook is shared across scenes, so scenes reuse identifiers and the window span absorbs part of the cut novelty.',
    ],
    framework: ['D/R/N decomposition', 'Frames', 'Basis frames'],
    notebooks: ['02_scene_sidecar.ipynb'],
  },
  {
    id: 'driving-vla',
    icon: '🚗',
    name: 'Autonomous driving',
    tagline: 'A driving stack that can say what changed on the road — and show the evidence.',
    hosts: ['Qwen-Drive-1.0-4B perception tokens', 'BEV perception head and a planning expert'],
    problem:
      'Perception tokens arrive frame by frame and nothing persists. The planner is never told that the scene changed, and no one can list which perception tokens left and which arrived.',
    approach:
      'Each frame becomes a state. Restoration is exact, and every state carries a shift warning built from the state decomposition, so the hand-off to the planner is the restored tokens plus a warning it can act on.',
    measured: [
      { value: '1.0', label: 'loop accuracy — ordered and at hash level, no tolerance' },
      { value: '0.0', label: 'waypoint disagreement between original and restored encodings' },
      { value: '3/3', label: 'scene cuts flagged by the transition line' },
      { value: '0.261', label: 'planning error in metres, the host planner against ground truth' },
    ],
    evidence: [
      'The loop closes exactly: the exact token set at hash level, and the original order at positional level. The encodings go back to the host unchanged.',
      'The hand-off is a pair — the restored tokens and a warning. Restoration stays exact, so the warning is the only thing the decoder has to act on.',
      'The warning is explainable rather than a bare flag: for every warned frame the side-car restores the exact departed, retained and new token sets behind it. One warned frame reported 130 departed, 16 retained, 124 new.',
      'Four frames were warned, covering all three scene cuts plus one camera-view switch. The clean run of the same trajectory stays quiet.',
      'With a frozen basis the trajectory gets stable coordinates, and a spectrum over time separates periodic processes from aperiodic events: the camera cadence shows as a line, scene cuts are broadband.',
    ],
    caveats: [
      'Loop accuracy measures the state morphism, not driving quality. The two are reported separately.',
      'The lightweight waypoint probe is a stand-in for the host planner; its error against ground truth is 8.169 m, while the real planning expert reaches 0.261 m. Only the second number describes the host.',
      'Because the codebook is shared, the reliable cut line here is the departure decomposition. The residual and the step length are dominated by camera-view switches and ordinary in-frame variation.',
    ],
    framework: ['D/R/N decomposition', 'Frozen basis + spectrum', 'Shift warning'],
    notebooks: ['03_qwendrive_sidecar.ipynb'],
  },
  {
    id: 'perceptron-pyramid',
    icon: '🔺',
    name: 'Many streams, one state',
    tagline: 'Perception, ego state, navigation and planning as one decomposable state.',
    hosts: ['Cached perception tokens', 'Ego, navigation and planner streams'],
    problem:
      'A real system runs several streams in parallel — what it sees, where it is, where it is going, what it plans. Each stream has its own notion of "now", and there is no single state that explains them.',
    approach:
      'Each stream keeps one state per step and the system state is their union. That union can then be read three ways: against the previous step, against its own components, and against the ring basis.',
    measured: [
      { value: '48/48', label: 'the union reached by two independent paths has one key' },
      { value: '3/3', label: 'scene cuts found in the union state alone' },
      { value: '0.902', label: 'the perception stream share of the union state' },
      { value: '5.8', label: 'mean bits shared between components, out of the union' },
    ],
    evidence: [
      'The component decomposition shows where the union comes from: perception dominates at 90% of the union, while the ego, navigation and planner streams contribute coarse, scene-stable structure.',
      'Because a component is by definition a subset of the union, projecting the union onto its own components is degenerate. Trajectories use frozen or cumulative dimensions instead — the scope of a reading is set by how its frame was built.',
      'The lattice identities hold by construction: departed joined with retained equals the previous state, retained joined with new equals the current state, and departed and new never overlap.',
      'The same union structure can be inscribed twice — once as a lattice expression, once as an operational program — and the two inscriptions get different keys while describing the same structure. A key names an inscription, not a value.',
    ],
    caveats: [
      'The departed/retained/new frame is a moving frame: its coordinates describe one transition, so comparing across time only makes sense against a frozen reference transition.',
    ],
    framework: ['One universe', 'Joined perceptrons', 'Frame readings'],
    notebooks: ['04_perceptron_pyramid.ipynb'],
  },
  {
    id: 'generative-latents',
    icon: '🎨',
    name: 'Image generation',
    tagline: 'Watching a denoising run, and catching the step where the latent jumps.',
    hosts: ['A rectified-flow sampler (synthetic stand-in for the real MMDiT host)'],
    problem:
      'A denoising run is a trajectory of latents. Nothing records it as state, so there is no way to ask which step changed the latent structurally or to detect an injected disturbance.',
    approach:
      'Every denoising step becomes a state. The run is scored on two levels — whether the encodings come back intact, and how well the codebook inverse reconstructs the latent — and a shift warning is raised when the state changes structurally.',
    measured: [
      { value: '1.0', label: 'loop accuracy across every step, ordered and at hash level' },
      { value: 'step 16', label: 'the injected disturbance — the warning fires exactly there' },
      { value: '29/29', label: 'steps that were structural events, not just new content' },
      { value: '0%', label: 'false warnings on the clean run of the same trajectory' },
    ],
    evidence: [
      'The disturbance detection is exact and silent when it should be: the disturbed run warns at step 16, the clean run produces no warning at all.',
      'The basis grows as the run accumulates, so a vector measured early lives in an earlier coordinate system. The side-car stores the basis frames themselves and re-reads any step in any frame, which puts the whole run in one coordinate system.',
      'Re-reading a step in a frame it was not measured in has a measurable cost, reported per step as the spill — the bits that frame cannot see.',
      'Every step of this run changed the basis, which makes the run a sequence of structural events. A basis change is a legitimate second commit condition alongside new content.',
    ],
    caveats: [
      'The host here is a deterministic synthetic sampler, not the production one — the mechanism is what is being measured.',
      'Reconstruction quality is low by construction because the codebook is random: it is defined and measurable now, and becomes an image metric once a trained decoder exists.',
    ],
    framework: ['Basis frames', 'Commit conditions', 'Two-level scoring'],
    notebooks: ['05_flux_sidecar.ipynb'],
  },
  {
    id: 'operational-graph',
    icon: '⚙️',
    name: 'Boot and recovery',
    tagline: 'The graph boots like a system, and restores itself instead of replaying.',
    hosts: ['No host — the operational graph driven from its command line'],
    problem:
      'After a failure, most systems replay work to get back to where they were. That cost grows with how long the system has been running.',
    approach:
      'A content-addressed boot file defines the values, programs and links, and the machine picks up its persisted state from the top of the stack rather than recomputing it.',
    measured: [
      { value: '0', label: 'operations needed on the restart — the state came from the stack' },
      { value: '23', label: 'stack entries restored from the persistent store' },
      { value: 'identical', label: 'the state key before and after the restart' },
      { value: '12', label: 'operations on the first boot, bounded by the fire budget' },
    ],
    evidence: [
      'The demo graph joins two values and then feeds its own output back into itself — a feedback cycle, bounded by an explicit fire budget so the run terminates.',
      'The fire sequence is deterministic: the join fires once, then the feedback operation fires repeatedly. Two runs of the same graph produce the same order.',
      'On the restart, no boot file is supplied, no budget is supplied and no commit store is requested. The machine finds the default boot file, restores the persisted stack and reports quiescence — zero operations.',
      'The boot report is a single auditable object: the boot file key, the compiled graph, the fire log, the stack from bottom to top, the state at the top, and the commits.',
    ],
    caveats: [
      'The demonstration graph is deliberately small — this shows the boot, dispatch and recovery mechanism rather than a scale result.',
    ],
    framework: ['Operational graph', 'Recovery as a pop'],
    notebooks: ['06_ewm_ops_boot.ipynb'],
  },
  {
    id: 'jepa-cooperation',
    icon: '🔮',
    name: 'World-model cooperation',
    tagline: 'A predictive encoder and the lattice, agreeing on one state.',
    hosts: ['A V-JEPA encoder and predictor'],
    problem:
      'A predictive world model produces continuous patch encodings and predicted encodings. Nothing outside the model keeps them as addressable state, tells you what is new, or shows what the predictor was actually given.',
    approach:
      'The encoder output is quantized into a token stream and ingested; the state is restored back to the predictor in its original order. The two sides stay one actor each, with a single boundary between them.',
    measured: [
      { value: '1.0', label: 'set accuracy — the state holds every token the host produced' },
      { value: '=True', label: 'the same stream ingested twice returns the same keys' },
      { value: 'True', label: 'output equals input — a three-stage chain reaches a fixed point' },
      { value: 'True', label: 'an unseen token cannot change the gated state' },
    ],
    evidence: [
      'Ordered restoration walks the n-gram window chain across the three channels, so the ambiguity left by the narrowest channel is resolved by the wider ones.',
      'Chaining three of these units and running them as one program converges to a fixed point: the chain hands back exactly what it was given, with no change detected on the final pass.',
      'Two agents consuming the same stream receive the same reference rather than a copy, and they fire in a deterministic order.',
      'Grounding is one-sided in a way that can be demonstrated: injecting a token the codebook cannot express produces an identical gated state. The gate keeps only what the codebook can express.',
      'The temporal decomposition on this stream keeps a running accountability of replacement — how much of the state is genuinely new against how much departed.',
    ],
    caveats: [
      'Two restoration numbers are reported and should not be conflated: the state holds every token at set level, while the ordered path used here restores sequence only faintly. The first number is the state; the second is a specific ordering walk.',
    ],
    framework: ['IICA morphisms', 'Holographic memory', 'One-sided grounding'],
    notebooks: ['07_jepa_ewm_state_machine_cooperation.ipynb'],
  },
  {
    id: 'multi-model',
    icon: '🧩',
    name: 'Several models, one state',
    tagline: 'One state over several models — including the record of which model answered.',
    hosts: ['Three small language models', 'A typed-decision model as router'],
    problem:
      'Modern systems run several models at once. Which one holds the memory, how do you compare what they are doing, and who decides which model answers this request?',
    approach:
      'The system state is the union of the per-model states. A similarity vector against the per-model ring gives one trajectory for the whole system, and the loop can be closed by feeding the materialized state back in.',
    measured: [
      { value: '3', label: 'real models behind one union state, keys agreeing 32/32' },
      { value: '16', label: 'the query cycle the side-car discovers from token streams alone' },
      { value: '0.000', label: 'prediction error of a cycle model trained on the first pass only' },
      { value: '0.750', label: 'majority baseline for the commit signal, recovered at 1.000' },
    ],
    evidence: [
      'Nothing in the apparatus knows there are several models: each is just a stream of tokens, and the union is a lattice join. The same state is reached by two independent paths with one key.',
      'Each model gets its own ring, so the trajectory of the whole system is a vector of similarities, one coordinate per model. The three models behave measurably differently — one re-indexes almost every step, another only in bursts.',
      'The side-car discovers the structure of the input cycle from token streams alone: the trajectory repeats exactly at the cycle length, its basis generations saturate, and a model trained on the first pass predicts the second with zero error.',
      'The recursion closes: materializing the per-model states and treating them as a second level of streams reproduces the same union, the same decomposition and the same ring. The lattice is one universe at every level.',
      'The loop can be closed around the apparatus without changing it — the materialized state, in restored order, is fed back into the prompts, and a predictor chooses the next query by expected novelty.',
      'Which model answers can itself become state. A typed-decision router returns a choice with probabilities and a confidence; when the record is lowered into tokens and ingested, the state remembers not only the answer but who was chosen and how confidently. A confidence floor routes low-confidence decisions to a fallback model.',
    ],
    caveats: [
      'The router ran against a deterministic mock with the same typed interface as the live model, because the hosted key was not available. The interface is what the notebook demonstrates.',
      'The learned selector did not beat the simple running-average selector in this run: its ratio to the best single predictor was 1.109 against 0.952. The honest reading is that the selection layer is not yet earning its keep.',
      'The prediction baselines are periodic because greedy decoding makes the input cycle deterministic. Sampled decoding is the harder case and is the next step, not a result.',
    ],
    framework: ['One universe', 'Recursion', 'Learned selection', 'Decision-in-state'],
    notebooks: [
      '08_multi_llm_sidecar.ipynb',
      '09_multi_llm_sidecar_real.ipynb',
      '10_multi_llm_sidecar_loop.ipynb',
      '11_multi_llm_sidecar_predictor.ipynb',
      '12_multi_llm_sidecar_learned_selector.ipynb',
      '13_multi_llm_sidecar_jev_router.ipynb',
      '14_multi_llm_sidecar_jev_patterns.ipynb',
    ],
  },
  {
    id: 'heterogeneous-modalities',
    icon: '🌐',
    name: 'Different modalities, one memory',
    tagline: 'Seeing, acting and predicting kept in a single state.',
    hosts: ['A document-vision encoder', 'A vision-language-action policy', 'A video world model'],
    problem:
      'A system that reads, acts and predicts runs three different models with three different kinds of output. Keeping three memories means never being able to ask what the system as a whole now knows.',
    approach:
      'Document tokens, action chunks and patch encodings are captured in their native environments and written to one common form. The same state apparatus ingests all three without modification.',
    measured: [
      { value: '3', label: 'modalities in one union state' },
      { value: '1.0', label: 'the union reached by two paths is identical' },
      { value: '80 → 672', label: 'union state growth over 24 frames of the three streams' },
      { value: '254', label: 'tokens restored, in order, from the final memory' },
    ],
    evidence: [
      'The capture comes first and lives outside the state apparatus: each stream is produced in its own environment and written in the same standard token form. The apparatus never sees a model.',
      'The pattern matrix over the three streams and the replacement indicators carry over unchanged from the single-modality work — the machinery does not know or care where the tokens came from.',
      'The union of three modalities still decomposes: what left, what stayed and what arrived are computed on the combined state exactly as they are on one stream.',
      'The final state materializes back to a readable, ordered token collection, which is what makes the memory inspectable rather than opaque.',
    ],
    caveats: [
      'The three streams have different natural lengths. The action chunk is short and is padded with its final state so the frames align, so frame counts here are aligned rather than naturally equal.',
      'These captures are small: this demonstrates that one apparatus spans modalities, not throughput.',
    ],
    framework: ['Encoding-agnostic boundary', 'D/R/N decomposition', 'Materialized memory'],
    notebooks: ['15_hetero_sidecar_ocr_vla_jepa.ipynb'],
  },
];
